import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';

import DisableDeleteActionDialog from '../../dialogs/DisableDeleteActionDialog';
import { MonitoredDisableDeleteMsg } from './InfrastructureHelper';

/**
 * Disable/Delete confirmation dialog for discovered infrastructure items
 * Shows 3-button dialog (Cancel/Delete/Disable) for items in discovery scope
 * Used by both List and Detail pages
 *
 * Uses React.forwardRef pattern to expose imperative handle methods.
 * Parent components can call ref.current.open(idCount, discoveredCount) to show the dialog.
 *
 * @param {Object} props
 * @param {Function} props.handleDeleteDataTableAction - Callback when delete is confirmed
 * @param {Function} props.handleDisableDataTableAction - Callback when disable is chosen
 * @param {React.Ref} ref - Forwarded ref with imperative handle methods: open(idCount, discoveredCount), close()
 *
 * @example
 * const dialogRef = useRef();
 * <DisableDeleteConfirmDialog ref={dialogRef} handleDeleteDataTableAction={...} handleDisableDataTableAction={...} />
 * // Later: dialogRef.current.open(5, 3); // Show dialog for 5 items, 3 discovered
 */
const DisableDeleteConfirmDialog = forwardRef(({ handleDeleteDataTableAction, handleDisableDataTableAction }, ref) => {
    const [showDialog, setShowDialog] = useState(false);
    const [idCount, setIdCount] = useState(0);
    const [discoveredCount, setDiscoveredCount] = useState(0);

    useImperativeHandle(ref, () => ({
        open: (count, discovered) => {
            setIdCount(count);
            setDiscoveredCount(discovered);
            setShowDialog(true);
        },
        close: () => setShowDialog(false),
    }));

    const renderConfirmMsg = () => <MonitoredDisableDeleteMsg idCount={idCount} discoveredCount={discoveredCount} />;

    return (
        <DisableDeleteActionDialog
            open={showDialog}
            setOpen={setShowDialog}
            onConfirm={handleDeleteDataTableAction}
            onDisable={handleDisableDataTableAction}
            label="DELETE"
            confirmMsg={renderConfirmMsg}
        />
    );
});

DisableDeleteConfirmDialog.displayName = 'DisableDeleteConfirmDialog';

DisableDeleteConfirmDialog.propTypes = {
    handleDeleteDataTableAction: PropTypes.func.isRequired,
    handleDisableDataTableAction: PropTypes.func.isRequired,
};

export default DisableDeleteConfirmDialog;
